import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

interface TooltipProps {
    children: React.ReactNode;
    tooltipText: string;
}

export const Tooltip: React.FC<TooltipProps> = ({ children, tooltipText }) => {
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLSpanElement>(null);
    const isHoveringRef = useRef({ trigger: false, tooltip: false });
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const cancelHide = useCallback(() => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    }, []);

    const scheduleHide = useCallback(() => {
        cancelHide();
        hideTimeoutRef.current = setTimeout(() => {
            if (!isHoveringRef.current.trigger && !isHoveringRef.current.tooltip) {
                setVisible(false);
            }
        }, 100);
    }, [cancelHide]);

    useEffect(() => {
        return () => cancelHide();
    }, [cancelHide]);

    useEffect(() => {
        if (!visible || !triggerRef.current) return;

        const rect = triggerRef.current.getBoundingClientRect();
        setCoords({
            top: rect.top - 10,
            left: rect.left + rect.width / 2,
        });
    }, [visible]);

    // WCAG 1.4.13: dismiss on Escape key
    useEffect(() => {
        if (!visible) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setVisible(false);
                triggerRef.current?.blur();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [visible]);

    // WCAG 1.4.13: tooltip is hoverable — pointer events enabled, hover tracked
    const portalTooltip = visible ? (
        <div
            role="tooltip"
            onMouseEnter={() => {
                isHoveringRef.current.tooltip = true;
                cancelHide();
            }}
            onMouseLeave={() => {
                isHoveringRef.current.tooltip = false;
                scheduleHide();
            }}
            style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                transform: 'translate(-50%, -100%)',
                zIndex: 999999,
                padding: '8px 12px',
                backgroundColor: '#333',
                color: 'white',
                borderRadius: 6,
                whiteSpace: 'pre-line',
                minWidth: '200px',
                textAlign: 'left',
                opacity: 1,
                transition: 'opacity 0.15s ease',
                boxShadow: '0px 4px 12px rgba(0,0,0,0.25)',
            }}
        >
            {tooltipText}
        </div>
    ) : null;

    return (
        <>
            <span
                ref={triggerRef}
                tabIndex={0}
                onMouseEnter={() => {
                    isHoveringRef.current.trigger = true;
                    cancelHide();
                    setVisible(true);
                }}
                onMouseLeave={() => {
                    isHoveringRef.current.trigger = false;
                    scheduleHide();
                }}
                onFocus={() => setVisible(true)}
                onBlur={() => {
                    isHoveringRef.current.trigger = false;
                    scheduleHide();
                }}
                style={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
                {children}
            </span>

            {ReactDOM.createPortal(portalTooltip, document.body)}
        </>
    );
};
